import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export const useNavbarInteraction = () => {
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const [dropdownLeft, setDropdownLeft] = useState<number | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeButtonRef = useRef<HTMLButtonElement | null>(null);

  const handleNavigation = (path: string) => {
    window.scrollTo(0, 0);
    navigate(path);
    setActiveMenu(null);
    setActiveCategory(null);
  };

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeft(scrollLeft > 0);
    setShowRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  const updateDropdownPosition = (btn: HTMLButtonElement | null) => {
    activeButtonRef.current = btn;
    const container = dropdownRef.current;
    const scroller = scrollRef.current;
    if (!btn || !container || !scroller) return;

    const containerRect = container.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();

    // 1. Calculate ideal left position relative to container
    let left = btnRect.left - containerRect.left;

    // 2. Determine dropdown width (measured or estimated)
    const dropdownEl = container.querySelector('.absolute.z-50') as HTMLElement | null;
    const dropdownWidth = dropdownEl
      ? (dropdownEl.getBoundingClientRect().width || dropdownEl.offsetWidth || 240)
      : 240;
    const viewportWidth = window.innerWidth;

    // 3. Prevent overflow on the right edge of viewport
    const idealViewportRight = containerRect.left + left + dropdownWidth;
    if (idealViewportRight > viewportWidth) {
      const overflow = idealViewportRight - viewportWidth;
      left = left - overflow - 8; // 8px margin from right edge
    }

    // 4. Prevent overflow on the left edge of viewport
    const finalViewportLeft = containerRect.left + left;
    if (finalViewportLeft < 8) {
      left = left + (8 - finalViewportLeft);
    }

    setDropdownLeft(left);
  };

  const handleMenuEnter = (menu: string) => {
    setActiveMenu(menu);
  };

  const handleMenuLeave = () => {
    setActiveMenu(null);
    setActiveCategory(null);
    setDropdownLeft(null);
  };

  const handleCategoryEnter = (category: string) => {
    setActiveCategory(category);
  };

  const handleCategoryLeave = () => {
    setActiveCategory(null);
  };

  const handleSubmenuLeave = () => {
    setActiveMenu(null);
    setActiveCategory(null);
    setDropdownLeft(null);
  };

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === "left" ? -240 : 240,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (!activeMenu) {
      activeButtonRef.current = null;
    }
  }, [activeMenu]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    checkScroll();

    const onScroll = () => {
      checkScroll();
      if (activeButtonRef.current) {
        updateDropdownPosition(activeButtonRef.current);
      }
    };

    const onResize = () => {
      checkScroll();
      if (activeButtonRef.current) {
        updateDropdownPosition(activeButtonRef.current);
      }
    };

    el.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
    };
  }, [activeMenu]);

  useEffect(() => {
    const container = dropdownRef.current;
    if (!container || !activeMenu) return;

    const handleLayoutChange = () => {
      if (activeButtonRef.current) {
        updateDropdownPosition(activeButtonRef.current);
      }
    };

    // Observe children mutations (dropdown mounted/unmounted or content modified)
    const mutationObserver = new MutationObserver(() => {
      handleLayoutChange();
      
      const dropdownEl = container.querySelector('.absolute.z-50') as HTMLElement | null;
      if (dropdownEl) {
        resizeObserver.disconnect();
        resizeObserver.observe(dropdownEl);
      }
    });

    // Observe dropdown element resizes
    const resizeObserver = new ResizeObserver(() => {
      handleLayoutChange();
    });

    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
    });

    const dropdownEl = container.querySelector('.absolute.z-50') as HTMLElement | null;
    if (dropdownEl) {
      resizeObserver.observe(dropdownEl);
    }

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [activeMenu]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setActiveMenu(null);
        setActiveCategory(null);
        setDropdownLeft(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return {
    dropdownRef,
    scrollRef,
    activeMenu,
    activeCategory,
    showLeft,
    showRight,
    dropdownLeft,
    handleNavigation,
    handleMenuEnter,
    handleMenuLeave,
    handleCategoryEnter,
    handleCategoryLeave,
    handleSubmenuLeave,
    setHoveredCategory: setActiveCategory,
    updateDropdownPosition,
    scroll,
  };
};
